/*======================================================================================================================
  * Bluebell: src/app/state/file-exchange/file-exchange.effects.ts
  * Last Edited: 21/02/2025
  * Copyright 2025 Bramble CFD LTD
  * The contents of this file are NOT for redistribution
  * See AUTHORS for list of developers on this project
 =====================================================================================================================*/
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EffectHandlerService } from '@services/effect-handler.service';
import { ApiService } from '@state/api.service';
import { selectFileExchangeData } from '@state/file-exchange/file-exchange.selectors';
import { TreeNode } from 'primeng/api';
import { map, mergeMap, switchMap } from 'rxjs';
import { FileExchangeActions } from '@state/file-exchange/file-exchange.actions';
import { FileExchangeData } from './file-exchange.interfaces';


@Injectable()
export class FileXEffects {
  private actions$ = inject(Actions);
  private api = inject(ApiService);
  private store = inject(Store);
  private handler = inject(EffectHandlerService);


  fileExchangeData = this.store.selectSignal(selectFileExchangeData);

    getFileXData$ = createEffect(() => this.actions$.pipe(
        ofType(
          FileExchangeActions.list,
          FileExchangeActions.listQuietly
        ),
        switchMap(() => this.api.fileExchangeRetrieveData().pipe(
          this.handler.effectResponse({
            success: (data: FileExchangeData) => FileExchangeActions.listSuccess(data),
            retry: FileExchangeActions.listQuietly(),
            failure: FileExchangeActions.listFailure(),
            failureMessage: 'Could not retrieve file exchange data'
          })
        )),
    ));

    makeDir$ = createEffect(() => this.actions$.pipe(
        ofType(FileExchangeActions.makeDir),
        map((action: any) => action.payload),
        switchMap(({ uuid, path }: { uuid: string, path: string }) =>
            this.api.fileExchangeMakeDirectory(uuid, path).pipe(
              this.handler.effectResponse({
                retry: FileExchangeActions.makeDir({ uuid, path }),
                success: (response: any) => {
                  const newData: TreeNode = response.data;
                  const data: TreeNode[] = this.fileExchangeData().map(
                    (node: TreeNode) =>
                      node.data === newData.data ? newData : node
                  );
                  return FileExchangeActions.makeDirSuccess(Object.assign({}, response, { data }));
                },
                failure: FileExchangeActions.makeDirFailure(),
                failureMessage: 'Could not create directory'
              })
            )
        ),
    ));

    moveFileOrDir$ = createEffect(() => this.actions$.pipe(
        ofType(FileExchangeActions.move),
        map((action: any) => action.payload),
        switchMap(({ uuid, oldPath, newPath }: { uuid: string, oldPath: string, newPath: string }) =>
            this.api.fileExchangeMoveFile(uuid, oldPath, newPath).pipe(
              this.handler.effectResponse({
                retry: FileExchangeActions.move({ uuid, oldPath, newPath }),
                success: (response: any) => {
                  const newData: TreeNode = response.data;
                  const data: TreeNode[] = this.fileExchangeData().map(
                    (node: TreeNode) =>
                      node.data === newData.data ? newData : node
                  );
                  return FileExchangeActions.moveSuccess(Object.assign({}, response, { data }));
                },
                failure: FileExchangeActions.moveFailure(),
                failureMessage: 'Could not move file'
              })
            )
        ),
    ));

    removeFileOrDir$ = createEffect(() => this.actions$.pipe(
        ofType(FileExchangeActions.remove),
        map((action: any) => action.payload),
        mergeMap(({ uuid, path }: {uuid: string, path: string}) =>
            this.api.fileExchangeDeleteFile(uuid, path).pipe(
              this.handler.effectResponse({
                retry: FileExchangeActions.remove({ uuid, path }),
                success: (response: any) => {
                  const newData: TreeNode = response.data;
                  const data: TreeNode[] = this.fileExchangeData().map(
                    (node: TreeNode) =>
                      node.data === newData.data ? newData : node
                  );
                  return FileExchangeActions.removeSuccess(Object.assign({}, response, { data }));
                },
                failure: FileExchangeActions.removeFailure(),
                failureMessage: 'Could not delete file'
              })
            )
        )
    ));
}
